// ============================================================
// ExpressProjectSetup.jsx
//
// A branded, responsive course content page for the
// "Project Structure & Setup" lesson.
//
// Author: ProDev Linux Series (Backend Branch)
// Features: Interactive terminal blocks, directory mapping,
//           and a navigation bridge to the Express Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import FolderZipIcon         from "@mui/icons-material/FolderZip";
import TerminalIcon          from "@mui/icons-material/Terminal";
import CreateNewFolderIcon   from "@mui/icons-material/CreateNewFolder";
import SettingsIcon          from "@mui/icons-material/Settings";
import ConstructionIcon       from "@mui/icons-material/Construction";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressProjectSetup() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Architecture · Module 02"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Project <span>Structure & Setup</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        A clean codebase starts with a predictable architecture. In this module, we move from a single file to a professional Express project structure optimized for scalability.
                    </Typography>
                </Box>

                {/* STEP 1: INITIALIZATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Initialize Node.js" />
                    <CodeBlock lines={[
                        { cmd: "npm init -y", comment: "Create a default package.json file" },
                        { cmd: "npm install express", comment: "Install the Express framework" },
                    ]} />
                </Box>

                {/* STEP 2: FOLDER STRUCTURE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CreateNewFolderIcon />} label="2. Scalable Directory Layout" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        At ProDev, we follow the "Separation of Concerns" principle. Organize your files so that logic, routes, and data models live in their own homes.
                    </Typography>
                    <Paper sx={{ p: 2.5, background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, fontFamily: "monospace", color: BRAND.gold }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 14 }}>
                            express-app/<br/>
                            ├── controllers/ <span style={{color: BRAND.muted}}># Logic for handling requests</span><br/>
                            ├── routes/      <span style={{color: BRAND.muted}}># Endpoint definitions</span><br/>
                            ├── middleware/  <span style={{color: BRAND.muted}}># Request validation/auth</span><br/>
                            ├── models/      <span style={{color: BRAND.muted}}># Database schemas</span><br/>
                            ├── config/      <span style={{color: BRAND.muted}}># DB connections & Envs</span><br/>
                            ├── app.js       <span style={{color: BRAND.muted}}># Entry point</span><br/>
                            └── package.json
                        </Typography>
                    </Paper>
                </Box>

                {/* STEP 3: ENTRY POINT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="3. The Entry Point (app.js)" />
                    <CodeBlock lines={[
                        { cmd: "const express = require('express');", comment: "Import Express" },
                        { cmd: "const app = express();", comment: "Init App" },
                        { cmd: "app.use(express.json());", comment: "Middleware to parse JSON bodies" },
                        { cmd: "app.get('/', (req, res) => res.send('ProDev API Active'));", comment: "Basic Route" },
                        { cmd: "app.listen(3000, () => console.log('Server Active'));", comment: "Listener" },
                    ]} />
                </Box>

                {/* STEP 4: DEV TOOLS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsIcon />} label="4. Developer Workflow" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Use <strong style={{color: BRAND.white}}>Nodemon</strong> to automatically restart your server whenever you save a file.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "npm install --save-dev nodemon", comment: "Install as a dev dependency" },
                    ]} />
                    <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 1.5, mt: 2, fontWeight: 700 }}>Update scripts in package.json:</Typography>
                    <Box sx={{ p: 2, background: BRAND.codeBg, borderRadius: 2, border: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText, fontSize: 13 }}>
                            "scripts": {"{"}<br/>
                            &nbsp;&nbsp;"start": "node app.js",<br/>
                            &nbsp;&nbsp;"dev": "nodemon app.js"<br/>
                            {"}"}
                        </Typography>
                    </Box>
                </Box>

                {/* STEP 5: ECOSYSTEM */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ConstructionIcon />} label="5. Essential Toolbelt" />
                    <Grid container spacing={2}>
                        {[
                            { title: "dotenv", desc: "Keep secrets (API keys) out of your code via .env files." },
                            { title: "morgan", desc: "HTTP request logger to see incoming traffic in your console." },
                            { title: "cors", desc: "Enable Cross-Origin requests so your React frontend can talk to your API." },
                        ].map((tool) => (
                            <Grid item xs={12} md={4} key={tool.title}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{tool.title}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{tool.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: BRAND.muted, mb: 3 }}>Finished setting up your environment?</Typography>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
